import { useId, cloneElement, isValidElement, Children, type ReactElement } from 'react'

interface FormFieldProps {
  label: string
  optional?: boolean
  error?: string
  children: React.ReactNode
}

/**
 * FormField wraps a form control with a label and error message, and
 * auto-binds <label htmlFor> to the input's id using useId().
 *
 * The first valid React element child is treated as the input and receives
 * the generated id (unless it already has one), plus `aria-invalid` /
 * `aria-describedby` tied to the error text when an error is present.
 * Any further children are rendered untouched so callers can include helper
 * text, icons, or other siblings alongside the input.
 */
export function FormField({ label, optional, error, children }: FormFieldProps) {
  const inputId = useId()
  const errorId = useId()

  let enhanced = false
  // The label must point at the input's REAL id — callers may pass their own
  // (LoginPage/SignupPage do), and htmlFor={inputId} would then dangle.
  let controlId = inputId
  const mappedChildren = Children.map(children, (child) => {
    if (!enhanced && isValidElement(child)) {
      enhanced = true
      const el = child as ReactElement<Record<string, unknown>>
      const existingDescribedBy = (el.props as { 'aria-describedby'?: string })['aria-describedby']
      controlId = (el.props as { id?: string }).id ?? inputId
      return cloneElement(el, {
        id: controlId,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': error ? [errorId, existingDescribedBy].filter(Boolean).join(' ') : existingDescribedBy,
      })
    }
    return child
  })

  return (
    <div>
      <label htmlFor={controlId} className="block text-sm text-muted mb-1">
        {label}
        {optional && <span className="text-faint"> (optional)</span>}
      </label>
      {mappedChildren}
      {error && (
        <p id={errorId} className="text-sm text-error-text mt-1">
          {error}
        </p>
      )}
    </div>
  )
}
